import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTagsStore } from '@/stores/tagsStore';
import { useHistoryStore, createHistoryEntry } from '@/stores/historyStore';
import { executeTagExtraction } from '@/services/tagsApi';
import { TextArea } from '@/components/TextArea';
import { Button } from '@/components/Button';
import { ResultPanel } from '@/components/ResultPanel/ResultPanel';
import { DebugPanel } from '@/components/DebugPanel/DebugPanel';
import { showToast } from '@/components/Toast/Toast';
import { readMarkdownFile, isValidMarkdownFile } from '@/utils/parseValuesFile';
import type { ThematicTagExtractionResponse } from '@/types/api';
import styles from './ThematicPage.module.css';

export function ThematicPage() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    thematic,
    setThematicLessonMarkdown,
    setThematicLoading,
    setThematicError,
    setThematicResponse,
    clearThematic,
  } = useTagsStore();

  const { addEntry } = useHistoryStore();

  const handleSubmit = useCallback(async () => {
    if (!thematic.lesson_markdown.trim()) {
      showToast('error', t('errors.lessonRequired'));
      return;
    }

    setThematicLoading(true);

    const result = await executeTagExtraction('thematic', thematic.lesson_markdown);

    // Add to history
    addEntry(
      createHistoryEntry(
        'thematic',
        result.requestPayload,
        result.ok ? (result.data as ThematicTagExtractionResponse) : null,
        result.responseHeaders || {},
        result.error || null
      )
    );

    if (result.ok && result.data) {
      setThematicResponse(result.data as ThematicTagExtractionResponse, result.traceId || null);
      showToast('success', t('toast.submitSuccess'));
    } else {
      setThematicError(result.error || t('errors.unknownError'));
    }
  }, [thematic.lesson_markdown, t, setThematicLoading, setThematicResponse, setThematicError, addEntry]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isValidMarkdownFile(file)) {
        showToast('error', t('errors.invalidFileFormat'));
        return;
      }

      try {
        const content = await readMarkdownFile(file);
        setThematicLessonMarkdown(content);
        showToast('success', t('toast.loadedFromFile'));
      } catch {
        showToast('error', t('errors.fileReadError'));
      }

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [t, setThematicLessonMarkdown]
  );

  const isSubmitDisabled = !thematic.lesson_markdown.trim() || thematic.loading;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('pages.thematic.title')}</h1>
        <p className={styles.description}>{t('pages.thematic.description')}</p>
      </header>

      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className={styles.field}>
          <TextArea
            label={t('form.lessonMarkdown')}
            value={thematic.lesson_markdown}
            onChange={(e) => setThematicLessonMarkdown(e.target.value)}
            placeholder={t('form.lessonMarkdownPlaceholder')}
            rows={12}
            disabled={thematic.loading}
          />
          <div className={styles.fileUpload}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,.markdown"
              onChange={handleFileUpload}
              className={styles.fileInput}
              id="markdown-upload"
            />
            <label htmlFor="markdown-upload" className={styles.fileLabel}>
              {t('form.uploadMarkdown')}
            </label>
            <span className={styles.fileHint}>{t('form.supportedMarkdownFormats')}</span>
          </div>
        </div>

        <div className={styles.actions}>
          <Button type="submit" disabled={isSubmitDisabled} loading={thematic.loading}>
            {t('common.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={clearThematic} disabled={thematic.loading}>
            {t('common.clear')}
          </Button>
        </div>
      </form>

      <ResultPanel
        response={thematic.lastResponse}
        traceId={thematic.lastTraceId}
        error={thematic.error}
        loading={thematic.loading}
      />

      <DebugPanel
        requestPayload={thematic.lastResponse ? { lesson_markdown: thematic.lesson_markdown } : null}
        responsePayload={thematic.lastResponse}
        responseHeaders={thematic.lastTraceId ? { 'x-request-id': thematic.lastTraceId } : {}}
      />
    </div>
  );
}
